'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import { useSearchParams } from 'next/navigation';
import { useCallback, useRef, useState } from 'react';
import { SearchTopAdBanner } from '@/app/_components/ads';
import { usePageSearchBarVisibility, useSearchHistoryStore } from '@/app/_components/SearchBar';
import { DEFAULT_SEARCH_CATEGORY, isSearchCategory, SEARCH_CATEGORY_QUERY_KEY } from '@/app/_components/SearchBar/constants';
import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useAppBack } from '@/modules/wello-app/_hooks/useAppBack';
import { GTM_EVENT_TYPE, trackGtmEvent } from '@/utils/analytics/gtm';
import { AllSearch } from './_components/AllSearch';
import { DonationBusinessSearch } from './_components/DonationBusinessSearch';
import { FestivalSearch } from './_components/FestivalSearch/FestivalSearch';
import { GiftSearch } from './_components/GiftSearch';
import { HometownNewsSearch } from './_components/HometownNewsSearch';
import { LocalMarketSearch } from './_components/LocalMarketSearch';
import { PolicyInfoSearch } from './_components/PolicyInfoSearch';
import { PolicySearch } from './_components/PolicySearch';
import { SearchPageHeader } from './_components/SearchPageHeader';
import { useIntegratedSearchFilter } from './_hooks/useIntegratedSearchFilter';
import { usePolicyFilter } from './_hooks/usePolicyFilter';
import { UrlFilterControllerProvider, useCategoryFilterCount } from './_hooks/useUrlFilterController';
import { SECTION_HEADER_MAX_W } from './constants';
export default function SearchPage() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <UrlFilterControllerProvider><SearchPageContent /></UrlFilterControllerProvider>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function SearchPageContent() {
  const $ = _c(74);
  useAppBack();
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const searchParams = useSearchParams();
  const resultsSectionRef = useRef(null);
  const {
    state,
    setKeyword,
    toggleTargetCode,
    toggleProvisionCode,
    toggleJobCode,
    toggleAgeCode,
    resetFilters,
    setSort,
    setPage,
    activeFilterCount
  } = usePolicyFilter();
  const {
    state: integratedState,
    setRegionCode,
    setSubRegionCode,
    toggleGiftCategory,
    toggleGiftInstitution,
    toggleDonationInstitution,
    setDonationSort,
    setFestivalLclsSystm2,
    setFestivalRegionCode,
    setFestivalSubRegionCode,
    setFestivalRadius,
    setFestivalMonth,
    resetHometownNewsFilters,
    resetGiftFilters,
    resetDonationFilters,
    resetFestivalFilters,
    hometownNewsFilterCount,
    giftFilterCount,
    donationFilterCount,
    festivalFilterCount
  } = useIntegratedSearchFilter();
  const policyInfoFilterCount = useCategoryFilterCount("policy-info");
  const searchHistory = useSearchHistoryStore(_temp);
  const add = useSearchHistoryStore(_temp2);
  const remove = useSearchHistoryStore(_temp3);
  const clear = useSearchHistoryStore(_temp4);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const {
    ref: searchBarRef
  } = usePageSearchBarVisibility();
  let t0;
  if ($[0] !== searchParams) {
    const categoryParam = searchParams.get(SEARCH_CATEGORY_QUERY_KEY);
    t0 = isSearchCategory(categoryParam) ? categoryParam : DEFAULT_SEARCH_CATEGORY;
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const category = t0;
  const isAllCategory = category === "all";
  const isPolicyCategory = category === "policy";
  let t1;
  if ($[2] !== add || $[3] !== category || $[4] !== setKeyword) {
    t1 = keyword => {
      setKeyword(keyword);
      add(keyword);
      if (keyword && category !== "all") {
        trackGtmEvent(GTM_EVENT_TYPE.SEARCH, {
          search_type: "\uD1B5\uD569\uAC80\uC0C9",
          search_term: keyword,
          search_category: category
        });
      }
    };
    $[2] = add;
    $[3] = category;
    $[4] = setKeyword;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const handleSearch = t1;
  let t2;
  if ($[6] !== category || $[7] !== donationFilterCount || $[8] !== festivalFilterCount || $[9] !== giftFilterCount || $[10] !== hometownNewsFilterCount || $[11] !== policyInfoFilterCount) {
    const getIntegratedFilterCount = function getIntegratedFilterCount() {
      switch (category) {
        case "hometown-news":
          {
            return hometownNewsFilterCount;
          }
        case "policy-info":
          {
            return policyInfoFilterCount;
          }
        case "gifts":
          {
            return giftFilterCount;
          }
        case "hometown-business":
          {
            return donationFilterCount;
          }
        case "festival":
          {
            return festivalFilterCount;
          }
        default:
          {
            return 0;
          }
      }
    };
    t2 = getIntegratedFilterCount();
    $[6] = category;
    $[7] = donationFilterCount;
    $[8] = festivalFilterCount;
    $[9] = giftFilterCount;
    $[10] = hometownNewsFilterCount;
    $[11] = policyInfoFilterCount;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const integratedFilterCount = t2;
  const hasIntegratedFilter = category === "hometown-news" || category === "policy-info" || category === "gifts" || category === "hometown-business" || category === "festival";
  const filterCount = isAllCategory ? 0 : isPolicyCategory ? activeFilterCount : integratedFilterCount;
  let t3;
  if ($[13] !== hasIntegratedFilter || $[14] !== isAllCategory || $[15] !== isPolicyCategory) {
    t3 = !isAllCategory && (isPolicyCategory || hasIntegratedFilter) ? () => setIsFilterOpen(true) : undefined;
    $[13] = hasIntegratedFilter;
    $[14] = isAllCategory;
    $[15] = isPolicyCategory;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const onFilterClick = t3;
  let t4;
  if ($[17] !== isDesktopViewport) {
    t4 = () => {
      const resultsSection = resultsSectionRef.current;
      if (!resultsSection) {
        return;
      }
      const top = resultsSection.getBoundingClientRect().top + window.scrollY - (isDesktopViewport ? 24 : 56);
      window.scrollTo({
        top: Math.max(top, 0),
        behavior: "auto"
      });
    };
    $[17] = isDesktopViewport;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const scrollToResultsTop = t4;
  let t5;
  if ($[19] !== scrollToResultsTop || $[20] !== setPage) {
    t5 = page => {
      setPage(page);
      scrollToResultsTop();
    };
    $[19] = scrollToResultsTop;
    $[20] = setPage;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const handlePageChange = t5;
  let t6;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setIsFilterOpen(false);
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const closeFilter = t6;
  let t7;
  if ($[23] !== category || $[24] !== handlePageChange || $[25] !== integratedState || $[26] !== isFilterOpen || $[27] !== resetDonationFilters || $[28] !== resetFestivalFilters || $[29] !== resetFilters || $[30] !== resetGiftFilters || $[31] !== resetHometownNewsFilters || $[32] !== setDonationSort || $[33] !== setFestivalLclsSystm2 || $[34] !== setFestivalMonth || $[35] !== setFestivalRadius || $[36] !== setFestivalRegionCode || $[37] !== setFestivalSubRegionCode || $[38] !== setRegionCode || $[39] !== setSort || $[40] !== setSubRegionCode || $[41] !== state || $[42] !== toggleAgeCode || $[43] !== toggleDonationInstitution || $[44] !== toggleGiftCategory || $[45] !== toggleGiftInstitution || $[46] !== toggleJobCode || $[47] !== toggleProvisionCode || $[48] !== toggleTargetCode) {
    t7 = () => {
      switch (category) {
        case "all":
          {
            return <AllSearch keyword={state.keyword} />;
          }
        case "policy":
          {
            return <PolicySearch isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} state={state} onToggleTargetCode={toggleTargetCode} onToggleProvisionCode={toggleProvisionCode} onToggleJobCode={toggleJobCode} onToggleAgeCode={toggleAgeCode} onResetFilters={resetFilters} onSortChange={setSort} onPageChange={handlePageChange} />;
          }
        case "hometown-news":
          {
            return <HometownNewsSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} regionCode={integratedState.regionCode} subRegionCode={integratedState.subRegionCode} onRegionChange={setRegionCode} onSubRegionChange={setSubRegionCode} onReset={resetHometownNewsFilters} />;
          }
        case "festival":
          {
            return <FestivalSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} lclsSystm2={integratedState.festivalLclsSystm2} onSelectLclsSystm2={setFestivalLclsSystm2} regionCode={integratedState.festivalRegionCode} subRegionCode={integratedState.festivalSubRegionCode} onSelectRegion={setFestivalRegionCode} onSelectSubRegion={setFestivalSubRegionCode} radius={integratedState.festivalRadius} onSelectRadius={setFestivalRadius} month={integratedState.festivalMonth} onSelectMonth={setFestivalMonth} onReset={resetFestivalFilters} />;
          }
        case "policy-info":
          {
            return <PolicyInfoSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} />;
          }
        case "gifts":
          {
            return <GiftSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} categoryIds={integratedState.giftCategoryIds} institutionIds={integratedState.giftInstitutionIds} onToggleCategory={toggleGiftCategory} onToggleInstitution={toggleGiftInstitution} onReset={resetGiftFilters} />;
          }
        case "hometown-business":
          {
            return <DonationBusinessSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} institutionIds={integratedState.donationInstitutionIds} sort={integratedState.donationSort} onToggleInstitution={toggleDonationInstitution} onSortChange={setDonationSort} onReset={resetDonationFilters} />;
          }
        case "welmarket":
          {
            return <LocalMarketSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} />;
          }
      }
    };
    $[23] = category;
    $[24] = handlePageChange;
    $[25] = integratedState;
    $[26] = isFilterOpen;
    $[27] = resetDonationFilters;
    $[28] = resetFestivalFilters;
    $[29] = resetFilters;
    $[30] = resetGiftFilters;
    $[31] = resetHometownNewsFilters;
    $[32] = setDonationSort;
    $[33] = setFestivalLclsSystm2;
    $[34] = setFestivalMonth;
    $[35] = setFestivalRadius;
    $[36] = setFestivalRegionCode;
    $[37] = setFestivalSubRegionCode;
    $[38] = setRegionCode;
    $[39] = setSort;
    $[40] = setSubRegionCode;
    $[41] = state;
    $[42] = toggleAgeCode;
    $[43] = toggleDonationInstitution;
    $[44] = toggleGiftCategory;
    $[45] = toggleGiftInstitution;
    $[46] = toggleJobCode;
    $[47] = toggleProvisionCode;
    $[48] = toggleTargetCode;
    $[49] = t7;
  } else {
    t7 = $[49];
  }
  const renderSection = t7;
  const t8 = SECTION_HEADER_MAX_W[category];
  let t9;
  if ($[50] !== t8) {
    t9 = cn("w-full max-w-screen-xl", t8);
    $[50] = t8;
    $[51] = t9;
  } else {
    t9 = $[51];
  }
  let t10;
  if ($[52] !== searchHistory) {
    t10 = searchHistory ?? [];
    $[52] = searchHistory;
    $[53] = t10;
  } else {
    t10 = $[53];
  }
  let t11;
  if ($[54] !== clear || $[55] !== filterCount || $[56] !== handleSearch || $[57] !== onFilterClick || $[58] !== remove || $[59] !== searchBarRef || $[60] !== state.keyword || $[61] !== t10) {
    t11 = <SearchPageHeader ref={searchBarRef} keyword={state.keyword} filterCount={filterCount} recentKeywords={t10} onSearch={handleSearch} onFilterClick={onFilterClick} onSelectRecent={handleSearch} onRemoveRecent={remove} onClearRecent={clear} />;
    $[54] = clear;
    $[55] = filterCount;
    $[56] = handleSearch;
    $[57] = onFilterClick;
    $[58] = remove;
    $[59] = searchBarRef;
    $[60] = state.keyword;
    $[61] = t10;
    $[62] = t11;
  } else {
    t11 = $[62];
  }
  let t12;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="mt-12 mobile-tablet:mt-0 mobile-tablet:px-16 mobile-tablet:py-12"><SearchTopAdBanner /></div>;
    $[63] = t12;
  } else {
    t12 = $[63];
  }
  let t13;
  if ($[64] !== t11 || $[65] !== t9) {
    t13 = <div className={t9}>{t11}{t12}</div>;
    $[64] = t11;
    $[65] = t9;
    $[66] = t13;
  } else {
    t13 = $[66];
  }
  let t14;
  if ($[67] !== renderSection) {
    t14 = renderSection();
    $[67] = renderSection;
    $[68] = t14;
  } else {
    t14 = $[68];
  }
  let t15;
  if ($[69] !== t14) {
    t15 = <div ref={resultsSectionRef} className="w-full">{t14}</div>;
    $[69] = t14;
    $[70] = t15;
  } else {
    t15 = $[70];
  }
  let t16;
  if ($[71] !== t13 || $[72] !== t15) {
    t16 = <div className="flex w-full flex-col items-center gap-12 p-40 mobile-tablet:gap-0 mobile-tablet:p-0">{t13}{t15}</div>;
    $[71] = t13;
    $[72] = t15;
    $[73] = t16;
  } else {
    t16 = $[73];
  }
  return t16;
}
function _temp4(state_3) {
  return state_3.clear;
}
function _temp3(state_2) {
  return state_2.remove;
}
function _temp2(state_1) {
  return state_1.add;
}
function _temp(state_0) {
  return state_0.data;
}